import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  adminUpdateTenantSettings,
  fetchAdminTenantSettings,
  resolveImageUrl,
} from "../services/api";

// -----------------------------------------------------------------------
// TenantSettingsPage — admin-only per-tenant settings.
//
// Replaces the old ManagerBrandingPage flow. Managers can no longer
// change their own country, languages, or branding; the founder
// controls these centrally so:
//   1. The language picker the customer sees stays consistent with
//      what each hotel actually serves (e.g. Sintalle in Hawassa gets
//      EN+AM+SID, no Afaan Oromo).
//   2. Brand assets (logo, cover image) stay clean and consistent.
//
// Reachable at /admin/tenants/:id/settings from TenantDetailPage's
// "Edit settings" button. Wrapped in <RequireAdmin> in App.js.
// -----------------------------------------------------------------------

const COUNTRY_OPTIONS = [
  { code: "ET", label: "Ethiopia 🇪🇹" },
  { code: "NL", label: "Netherlands 🇳🇱" },
  { code: "OTHER", label: "Other (English only)" },
];

// Languages the platform supports today. Order matters — this is the
// order checkboxes render in the UI and the order they end up in the
// stored array (English is always pinned first by the backend).
const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", note: "Always on (universal fallback)" },
  { code: "am", label: "አማርኛ (Amharic)", note: "Ethiopia — central/north" },
  { code: "om", label: "Afaan Oromoo", note: "Ethiopia — Oromia, Addis" },
  { code: "sid", label: "Sidaamu Afoo (Af Sidama)", note: "Ethiopia — Sidama / Hawassa" },
  { code: "ti", label: "ት ግር ኛ (Tigrinya)", note: "Ethiopia — Tigrinya-speaking hotel owners in Addis & Tigray" },
  { code: "nl", label: "Nederlands (Dutch)", note: "Netherlands diaspora restaurants" },
];

// Default language list per country — shown as a hint when the tenant
// has no explicit supported_languages override yet. Mirrors the
// fallback the customer app uses when the server returns null.
const DEFAULT_LANGS_BY_COUNTRY = {
  ET: ["en", "am", "om"],
  NL: ["en", "nl"],
  OTHER: ["en"],
};

export default function TenantSettingsPage() {
  const { id: rawId } = useParams();
  const tenantId = Number(rawId);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [tenant, setTenant] = useState(null);

  // Form state — separate from `tenant` so we can detect dirtiness.
  const [country, setCountry] = useState("ET");
  const [venueType, setVenueType] = useState("hotel");
  const [usingDefault, setUsingDefault] = useState(true);
  const [langs, setLangs] = useState(["en", "am", "om"]);
  // Staff-dashboard language set — independent of the guest list above.
  const [staffLangs, setStaffLangs] = useState(["en", "am"]);
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  // Per-tenant flag: does the customer see a live order-status badge?
  // Friend-test feedback #75. Default true so existing hotels behave
  // the same as before this column existed.
  const [showCustomerStatus, setShowCustomerStatus] = useState(true);
  // Tier context: can this venue use languages beyond English + Amharic?
  // (the all_languages feature, Pro+). Drives the visual gate below.
  const [canMultiLang, setCanMultiLang] = useState(true);
  const [tier, setTier] = useState(null);

  // Tax & invoice — fiscal identity printed on the guest invoice.
  const [taxMode, setTaxMode] = useState("vat");
  const [vatPct, setVatPct] = useState(15);
  const [totPct, setTotPct] = useState(2);
  const [pricesIncl, setPricesIncl] = useState(true);
  const [legalName, setLegalName] = useState("");
  const [tin, setTin] = useState("");
  const [vatRegNo, setVatRegNo] = useState("");
  const [bizAddress, setBizAddress] = useState("");
  const [bizPhone, setBizPhone] = useState("");
  const [invoicePrefix, setInvoicePrefix] = useState("");

  // Post-checkout Google review flow.
  const [googleReviewUrl, setGoogleReviewUrl] = useState("");
  const [reviewGate, setReviewGate] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // ---- load on mount ------------------------------------------------
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setLoadErr(null);
    fetchAdminTenantSettings(tenantId)
      .then((data) => {
        if (cancel) return;
        setTenant(data);
        const c = data.country || "ET";
        setCountry(c);
        setVenueType(data.venue_type || "hotel");
        const stored = Array.isArray(data.supported_languages)
          ? data.supported_languages
          : null;
        if (stored && stored.length > 0) {
          setUsingDefault(false);
          setLangs(stored);
        } else {
          setUsingDefault(true);
          setLangs(DEFAULT_LANGS_BY_COUNTRY[c] || ["en"]);
        }
        const staffStored = Array.isArray(data.staff_languages) && data.staff_languages.length
          ? data.staff_languages : null;
        setStaffLangs(staffStored || (stored && stored.length ? stored : ["en", "am"]));
        setLogoUrl(data.logo_url || "");
        setCoverUrl(data.cover_image_url || "");
        setShowCustomerStatus(data.show_customer_status !== false);
        setCanMultiLang(data.all_languages !== false);
        setTier(data.subscription_tier || null);
        hydrateTax(data);
      })
      .catch((e) => {
        if (cancel) return;
        setLoadErr(
          e?.response?.data?.detail || "Could not load tenant settings.",
        );
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => { cancel = true; };
  }, [tenantId]);

  // ---- helpers ------------------------------------------------------
  const hydrateTax = (d) => {
    setTaxMode(d.tax_mode || "vat");
    setVatPct(d.vat_pct != null ? d.vat_pct : 15);
    setTotPct(d.tot_pct != null ? d.tot_pct : 2);
    setPricesIncl(d.prices_include_vat !== false);
    setLegalName(d.legal_name || "");
    setTin(d.tin || "");
    setVatRegNo(d.vat_reg_no || "");
    setBizAddress(d.biz_address || "");
    setBizPhone(d.biz_phone || "");
    setInvoicePrefix(d.invoice_prefix || "");
    setGoogleReviewUrl(d.google_review_url || "");
    setReviewGate(d.review_gate === true);
  };

  const toggleLang = (code) => {
    if (code === "en") return;  // English is always on
    setUsingDefault(false);
    setLangs((prev) =>
      prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code],
    );
  };

  const toggleStaffLang = (code) => {
    if (code === "en") return;  // English is always on
    setStaffLangs((prev) =>
      prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code],
    );
  };

  // A language beyond the English+Amharic baseline that this venue's plan
  // can't unlock (the all_languages feature is Pro+).
  const langGated = (code) => !["en", "am"].includes(code) && !canMultiLang;
  const proBadge = (
    <span style={{
      marginLeft: 8, fontSize: ".68rem", color: "#fff", background: "#854F0B",
      borderRadius: 10, padding: "1px 8px", fontWeight: 700, letterSpacing: ".5px",
    }}>PRO</span>
  );

  const resetToDefault = () => {
    const defaults = DEFAULT_LANGS_BY_COUNTRY[country] || ["en"];
    setLangs(defaults);
    setUsingDefault(true);
  };

  const onCountryChange = (newCountry) => {
    setCountry(newCountry);
    // If the admin was on the default set, slide them to the new
    // country's defaults so the UI doesn't look stale.
    if (usingDefault) {
      setLangs(DEFAULT_LANGS_BY_COUNTRY[newCountry] || ["en"]);
    }
  };

  const save = async () => {
    setSaving(true);
    setSaveErr(null);
    setSavedAt(null);
    try {
      const patch = {
        country,
        venue_type: venueType,
        // Empty list -> backend clears the override and falls back to
        // country defaults; non-empty -> backend stores explicit list.
        supported_languages: usingDefault ? [] : langs,
        staff_languages: staffLangs,
        logo_url: logoUrl.trim(),
        cover_image_url: coverUrl.trim(),
        show_customer_status: showCustomerStatus,
        tax_mode: taxMode,
        vat_pct: Number(vatPct) || 0,
        tot_pct: Number(totPct) || 0,
        prices_include_vat: pricesIncl,
        legal_name: legalName.trim(),
        tin: tin.trim(),
        vat_reg_no: vatRegNo.trim(),
        biz_address: bizAddress.trim(),
        biz_phone: bizPhone.trim(),
        invoice_prefix: invoicePrefix.trim(),
        google_review_url: googleReviewUrl.trim(),
        review_gate: reviewGate,
      };
      const updated = await adminUpdateTenantSettings(tenantId, patch);
      setTenant(updated);
      setSavedAt(new Date());
      // Re-sync usingDefault from the server's response so the UI
      // reflects whether the override actually persisted.
      const stored = Array.isArray(updated.supported_languages)
        ? updated.supported_languages
        : null;
      if (stored && stored.length > 0) {
        setUsingDefault(false);
        setLangs(stored);
      } else {
        setUsingDefault(true);
        setLangs(DEFAULT_LANGS_BY_COUNTRY[updated.country] || ["en"]);
      }
      const staffStored = Array.isArray(updated.staff_languages) && updated.staff_languages.length
        ? updated.staff_languages : null;
      setStaffLangs(staffStored || (stored && stored.length ? stored : ["en", "am"]));
      setShowCustomerStatus(updated.show_customer_status !== false);
      setCanMultiLang(updated.all_languages !== false);
      setTier(updated.subscription_tier || null);
      hydrateTax(updated);
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  // ---- render -------------------------------------------------------
  if (loading) {
    return (
      <>
        <h1>Tenant settings</h1>
        <p className="muted">Loading…</p>
      </>
    );
  }
  if (loadErr) {
    return (
      <>
        <h1>Tenant settings</h1>
        <div className="error">{loadErr}</div>
        <p>
          <Link to={`/admin/tenants/${tenantId}`}>← Back to tenant</Link>
        </p>
      </>
    );
  }

  const effective = usingDefault ? (DEFAULT_LANGS_BY_COUNTRY[country] || ["en"]) : langs;

  return (
    <>
      <div style={{ marginBottom: 6 }}>
        <Link to={`/admin/tenants/${tenantId}`} className="muted">
          ← Back to {tenant?.name || "tenant"}
        </Link>
      </div>
      <h1 style={{ marginBottom: 4 }}>{tenant?.name || "Tenant"} — Settings</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        Country, languages, and branding. Admin-only.
      </p>

      {/* ============ VENUE TYPE ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Venue type</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          What this venue is. <b>Spa &amp; Gym</b> hides all hotel and
          restaurant screens and lands the manager on the wellness board —
          use it for standalone gyms, spas, salons and pools. Switching to
          Spa &amp; Gym turns the hotel + F&amp;B modules off (the manager can
          re-enable any later).
        </p>
        <div className="field">
          <select value={venueType} onChange={(e) => setVenueType(e.target.value)}>
            <option value="hotel">Hotel (rooms + F&amp;B)</option>
            <option value="restaurant">Restaurant / café</option>
            <option value="spa_gym">Spa &amp; Gym (wellness only)</option>
          </select>
        </div>
      </div>

      {/* ============ COUNTRY ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Country</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Where the hotel/restaurant operates. Determines the default
          language set when no explicit override is configured.
        </p>
        <div className="field">
          <select
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
          >
            {COUNTRY_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!canMultiLang && (
        <div className="card" style={{ marginBottom: 16, background: "#fff8e7", border: "1px solid #f0d38b", color: "#6c5a00" }}>
          ⚠ <b>This venue is on the {tier || "current"} plan.</b> Languages beyond
          <b> English + Amharic</b> require the <b>Professional</b> plan. Gated
          options are marked {proBadge} below and stay disabled until the plan is
          upgraded (the server enforces this on save too).
        </div>
      )}

      {/* ============ LANGUAGES ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Guest languages (customer app)</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          The languages shown in the customer-facing language picker.
          <b> English is always included.</b> Tigrinya, Sidama and Dutch are
          still draft (machine) quality — enable them for guests only if
          you're comfortable until native review. Languages beyond English +
          Amharic require the Professional plan.
        </p>

        <div
          style={{
            background: usingDefault ? "#fff8e7" : "#e8f4f4",
            border: "1px solid " + (usingDefault ? "#f0d38b" : "#99cccc"),
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: ".9rem",
          }}
        >
          {usingDefault ? (
            <>
              <b>Using country default:</b> {effective.join(", ").toUpperCase()}.
              {" "}Tick languages below to set an explicit per-tenant list
              (e.g. add SID for a Sidama hotel, remove OM if Afaan Oromo
              isn't needed).
            </>
          ) : (
            <>
              <b>Custom override active:</b> {effective.join(", ").toUpperCase()}.{" "}
              <button
                type="button"
                className="btn small"
                style={{ marginLeft: 8 }}
                onClick={resetToDefault}
              >
                Reset to country default
              </button>
            </>
          )}
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          {LANGUAGE_OPTIONS.map((opt) => {
            const checked = langs.includes(opt.code);
            const lockedOn = opt.code === "en";
            const gated = langGated(opt.code);
            const off = lockedOn || gated;
            return (
              <label
                key={opt.code}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 12px",
                  border: "1px solid #e2ddd5",
                  borderRadius: 8,
                  background: checked && !gated ? "#f6faf6" : "#fff",
                  cursor: off ? "not-allowed" : "pointer",
                  opacity: gated ? 0.55 : (lockedOn ? 0.85 : 1),
                }}
              >
                <input
                  type="checkbox"
                  checked={(checked && !gated) || lockedOn}
                  disabled={off}
                  onChange={() => toggleLang(opt.code)}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {opt.label}
                    {lockedOn && (
                      <span
                        style={{
                          marginLeft: 8, fontSize: ".75rem",
                          color: "#5c7c7f", fontWeight: 500,
                        }}
                      >
                        (always on)
                      </span>
                    )}
                    {gated && proBadge}
                  </div>
                  <div className="muted" style={{ fontSize: ".85rem" }}>
                    {opt.note}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Tigrinya heads-up — same placeholder-state caveat as sid */}
        {langs.includes("ti") && (
          <div
            style={{
              marginTop: 14,
              background: "#fff8e7",
              border: "1px solid #f0d38b",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: ".88rem",
              color: "#6c5a00",
            }}
          >
            ⚠ <b>Tigrinya (ti) is in placeholder state.</b> The UI strings
            currently show English text until proper translations are added.
            The menu-item input field for <code>name_ti</code> is available
            to the manager so dishes can be translated piecewise.
          </div>
        )}

        {/* Af Sidama heads-up */}
        {langs.includes("sid") && (
          <div
            style={{
              marginTop: 14,
              background: "#fff8e7",
              border: "1px solid #f0d38b",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: ".88rem",
              color: "#6c5a00",
            }}
          >
            ⚠ <b>Af Sidama (sid) is in placeholder state.</b> The UI strings
            currently show English text until proper translations are added.
            The menu-item input field for <code>name_sid</code> is available
            to the manager so dishes can be translated piecewise.
          </div>
        )}
      </div>

      {/* ============ STAFF DASHBOARD LANGUAGES ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Staff dashboard languages</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Languages offered to this venue's staff in the dashboard — set
          independently of the guest list. <b>English + Amharic are always
          included.</b> Tigrinya, Sidama and Dutch are draft (machine)
          quality until native review. Anything beyond English + Amharic
          requires the Professional plan (enforced on save).
        </p>
        <div style={{ display: "grid", gap: 10 }}>
          {LANGUAGE_OPTIONS.map((opt) => {
            const checked = staffLangs.includes(opt.code);
            const lockedOn = opt.code === "en" || opt.code === "am";
            const isDraft = ["ti", "sid", "nl"].includes(opt.code);
            const gated = langGated(opt.code);
            const off = lockedOn || gated;
            return (
              <label
                key={opt.code}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "10px 12px", border: "1px solid #e2ddd5",
                  borderRadius: 8, background: checked && !gated ? "#f6faf6" : "#fff",
                  cursor: off ? "not-allowed" : "pointer",
                  opacity: gated ? 0.55 : (lockedOn ? 0.85 : 1),
                }}
              >
                <input
                  type="checkbox"
                  checked={(checked && !gated) || lockedOn}
                  disabled={off}
                  onChange={() => toggleStaffLang(opt.code)}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {opt.label}
                    {lockedOn && (
                      <span style={{ marginLeft: 8, fontSize: ".75rem", color: "#5c7c7f", fontWeight: 500 }}>
                        (always on)
                      </span>
                    )}
                    {isDraft && (
                      <span style={{ marginLeft: 8, fontSize: ".75rem", color: "#8a6d00", fontWeight: 500 }}>
                        (draft — staff only)
                      </span>
                    )}
                    {gated && proBadge}
                  </div>
                  <div className="muted" style={{ fontSize: ".85rem" }}>{opt.note}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* ============ BRANDING ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Branding</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Logo and cover image shown on the customer menu, receipt, and
          rating screens. Paste Cloudinary URLs (or any public image URL).
        </p>

        <div className="field">
          <label>Logo URL (square 1:1)</label>
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://res.cloudinary.com/.../logo.png"
            style={{ width: "100%" }}
          />
          {logoUrl && (
            <div style={{ marginTop: 8 }}>
              <img
                src={resolveImageUrl(logoUrl)}
                alt="Logo preview"
                style={{
                  maxHeight: 90, maxWidth: 90, objectFit: "contain",
                  border: "1px solid #e2ddd5", borderRadius: 6, padding: 4,
                  background: "#fff",
                }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          )}
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <label>Cover image URL (banner 3:1)</label>
          <input
            type="text"
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            placeholder="https://res.cloudinary.com/.../cover.jpg"
            style={{ width: "100%" }}
          />
          {coverUrl && (
            <div style={{ marginTop: 8 }}>
              <img
                src={resolveImageUrl(coverUrl)}
                alt="Cover preview"
                style={{
                  maxHeight: 120, maxWidth: 360, objectFit: "cover",
                  border: "1px solid #e2ddd5", borderRadius: 6,
                  aspectRatio: "3 / 1",
                }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ============ CUSTOMER EXPERIENCE ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Customer experience</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Toggles that affect what the customer sees on their phone after
          they scan the QR and place an order.
        </p>

        <label
          style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            padding: 10, borderRadius: 8, background: "#fbf9f4",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showCustomerStatus}
            onChange={(e) => setShowCustomerStatus(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>
              Show live order status to customer
            </div>
            <div className="muted" style={{ fontSize: ".85rem", marginTop: 2 }}>
              When on, the customer's order confirmation page polls every
              ~12 seconds and shows the current status (sent to kitchen
              → being prepared → ready → served) in their chosen language.
              Turn off if your team prefers verbal communication and
              doesn't want customers staring at their phones for updates.
            </div>
          </div>
        </label>
      </div>

      {/* ============ TAX & INVOICE ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Tax &amp; invoice</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          The fiscal identity printed on the guest invoice. ODRA produces a
          compliant VAT / Turnover-tax invoice with a sequential number; where
          the venue uses an ERCA-accredited fiscal machine, it issues the legal
          receipt for the same amount.
        </p>

        <div className="field">
          <label>Tax type</label>
          <select value={taxMode} onChange={(e) => setTaxMode(e.target.value)}>
            <option value="vat">VAT (registered)</option>
            <option value="tot">Turnover tax</option>
            <option value="none">No tax line</option>
          </select>
        </div>

        {taxMode === "vat" && (
          <div className="field" style={{ marginTop: 12 }}>
            <label>VAT rate (%)</label>
            <input type="number" min="0" max="100" step="0.1" value={vatPct}
              onChange={(e) => setVatPct(e.target.value)} style={{ width: 160 }} />
          </div>
        )}
        {taxMode === "tot" && (
          <div className="field" style={{ marginTop: 12 }}>
            <label>Turnover-tax rate (%)</label>
            <input type="number" min="0" max="100" step="0.1" value={totPct}
              onChange={(e) => setTotPct(e.target.value)} style={{ width: 160 }} />
            <div className="muted" style={{ fontSize: ".82rem", marginTop: 4 }}>
              Typically 2% on goods, 10% on services — set what applies to this venue.
            </div>
          </div>
        )}

        {taxMode !== "none" && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 10, borderRadius: 8, background: "#fbf9f4", cursor: "pointer", marginTop: 12 }}>
            <input type="checkbox" checked={pricesIncl} onChange={(e) => setPricesIncl(e.target.checked)} style={{ marginTop: 3 }} />
            <div>
              <div style={{ fontWeight: 600 }}>Prices already include tax</div>
              <div className="muted" style={{ fontSize: ".85rem", marginTop: 2 }}>
                When on, room rates are tax-inclusive and the invoice shows the tax
                portion inside the total. When off, tax is added on top.
              </div>
            </div>
          </label>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 16 }}>
          <div className="field">
            <label>Registered business name</label>
            <input type="text" value={legalName} onChange={(e) => setLegalName(e.target.value)}
              placeholder={tenant?.name || "Legal name on invoice"} style={{ width: "100%" }} />
          </div>
          <div className="field">
            <label>TIN (Taxpayer ID)</label>
            <input type="text" value={tin} onChange={(e) => setTin(e.target.value)}
              placeholder="e.g. 0001234567" style={{ width: "100%" }} />
          </div>
          <div className="field">
            <label>VAT registration no.</label>
            <input type="text" value={vatRegNo} onChange={(e) => setVatRegNo(e.target.value)}
              placeholder="VAT reg. number" style={{ width: "100%" }} disabled={taxMode !== "vat"} />
          </div>
          <div className="field">
            <label>Business phone</label>
            <input type="text" value={bizPhone} onChange={(e) => setBizPhone(e.target.value)}
              placeholder="+251 …" style={{ width: "100%" }} />
          </div>
          <div className="field">
            <label>Business address</label>
            <input type="text" value={bizAddress} onChange={(e) => setBizAddress(e.target.value)}
              placeholder="Street, city" style={{ width: "100%" }} />
          </div>
          <div className="field">
            <label>Invoice number prefix</label>
            <input type="text" value={invoicePrefix} onChange={(e) => setInvoicePrefix(e.target.value)}
              placeholder="e.g. INV- (optional)" style={{ width: "100%" }} />
          </div>
        </div>
      </div>

      {/* ============ GUEST REVIEWS (GOOGLE) ============ */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Guest reviews (Google)</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Invite departing guests to review the hotel on Google — via an
          automatic Telegram message after check-out and a QR printed on the
          folio.
        </p>

        <div className="field">
          <label>Google review link</label>
          <input type="text" value={googleReviewUrl}
            onChange={(e) => setGoogleReviewUrl(e.target.value)}
            placeholder="https://g.page/r/…/review" style={{ width: "100%" }} />
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 10, borderRadius: 8, background: "#fbf9f4", cursor: "pointer", marginTop: 12 }}>
          <input type="checkbox" checked={reviewGate}
            onChange={(e) => setReviewGate(e.target.checked)} style={{ marginTop: 3 }} />
          <div>
            <div style={{ fontWeight: 600 }}>Review gate</div>
            <div className="muted" style={{ fontSize: ".85rem", marginTop: 2 }}>
              On: only guests who rate 4★ or higher are invited to Google;
              others go to private feedback. Off: all departing guests are
              invited.
            </div>
          </div>
        </label>
      </div>

      {/* ============ SAVE BAR ============ */}
      {saveErr && <div className="error">{saveErr}</div>}
      <div
        style={{
          display: "flex", gap: 10, alignItems: "center",
          padding: "16px 0", borderTop: "1px solid #e2ddd5",
          position: "sticky", bottom: 0, background: "#fbf9f4",
        }}
      >
        <button
          type="button"
          className="btn primary"
          disabled={saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save all changes"}
        </button>
        {savedAt && (
          <span className="muted">
            ✓ Saved at {savedAt.toLocaleTimeString()}.
          </span>
        )}
        <Link
          to={`/admin/tenants/${tenantId}`}
          style={{ marginLeft: "auto" }}
          className="muted"
        >
          Back to tenant detail →
        </Link>
      </div>
    </>
  );
}
